import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../../components/Header/Header';
import SeoMeta from '../../../components/SeoMeta/SeoMeta';
import ScrollToTopButton from '../../../components/ScrollToTopButton/ScrollToTopButton';
import LnatEditorialFooter from '../lnat/LnatEditorialFooter';
import TmuaEditorialFooter from '../tmua/TmuaEditorialFooter';
import UcatEditorialFooter from '../ucat/UcatEditorialFooter';
import EsatEditorialFooter from '../esat/EsatEditorialFooter';
import TaraEditorialFooter from '../tara/TaraEditorialFooter';
import OxbridgeEditorialFooter from '../oxbridge/OxbridgeEditorialFooter';
import MedicalSchoolEditorialFooter from '../medicalschool/MedicalSchoolEditorialFooter';
import DegreeApprenticeshipEditorialFooter from '../degreeapprenticeship/DegreeApprenticeshipEditorialFooter';
import PathbuddySiteFooter from '../../edu-landing/PathbuddySiteFooter';
import EduSiteHeader from '../../edu-landing/EduSiteHeader';
import {
  getExamHomePath,
  getExamKeyFromHost,
  getExamMethodPath,
  rewriteEditorialHtml,
} from './examContext';
import './EditorialPages.css';

const EXAM_FOOTERS = {
  lnat: LnatEditorialFooter,
  tmua: TmuaEditorialFooter,
  ucat: UcatEditorialFooter,
  esat: EsatEditorialFooter,
  tara: TaraEditorialFooter,
  oxbridge: OxbridgeEditorialFooter,
  medicalschool: MedicalSchoolEditorialFooter,
  degreeapprenticeship: DegreeApprenticeshipEditorialFooter,
  pathbuddy: PathbuddySiteFooter,
};

export function useEditorialNav(examKey = getExamKeyFromHost()) {
  const navigate = useNavigate();

  const goHome = useCallback(() => {
    navigate(getExamHomePath(examKey));
  }, [navigate, examKey]);

  const goPlans = useCallback(() => {
    if (examKey === 'pathbuddy') {
      navigate('/pricing');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const home = getExamHomePath(examKey);
    navigate(`${home}#pricing`);
    window.setTimeout(() => {
      const el = document.getElementById('pricing') || document.getElementById(`${examKey}-pricing`);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 80);
  }, [navigate, examKey]);

  const onBodyClick = useCallback((e) => {
    const a = e.target.closest('a[data-nav]');
    if (!a) return;
    e.preventDefault();
    const target = a.getAttribute('data-nav');
    if (target === 'plans') {
      goPlans();
      return;
    }
    if (target === 'method') {
      navigate(getExamMethodPath(examKey));
      return;
    }
    if (target === 'home') {
      goHome();
      return;
    }
    if (target) navigate(target);
  }, [goHome, goPlans, navigate, examKey]);

  return { navigate, goHome, goPlans, onBodyClick, examKey };
}

export function EditorialArticleShell({
  title,
  description,
  url,
  backLabel,
  onBack,
  eyebrow,
  heading,
  meta,
  bodyHtml,
  wide = false,
  children,
  examKey = getExamKeyFromHost(),
  schema,
  keywords,
}) {
  const { onBodyClick } = useEditorialNav(examKey);
  const html = rewriteEditorialHtml(bodyHtml);
  const Footer = useMemo(() => EXAM_FOOTERS[examKey] || LnatEditorialFooter, [examKey]);
  const onPathbuddy = examKey === 'pathbuddy';

  return (
    <div className={`lnat-ed-root${onPathbuddy ? ' lnat-ed-root--pathbuddy' : ''}`}>
      <SeoMeta title={title} description={description} url={url} schema={schema} keywords={keywords} />
      {onPathbuddy ? <EduSiteHeader /> : <Header variant="landing" />}

      <section className="lnat-ed-hero">
        <div className={`lnat-ed-container${wide ? '-wide' : ''} lnat-ed-hero-center${wide ? ' lnat-ed-insights-hero' : ''}`}>
          <button type="button" className="lnat-ed-back" onClick={onBack}>
            {backLabel}
          </button>
          {eyebrow && <span className="lnat-ed-eyebrow">{eyebrow}</span>}
          <h1>{heading}</h1>
          {meta && (
            <p className={wide ? 'lnat-ed-insights-sub' : 'lnat-ed-meta'}>{meta}</p>
          )}
        </div>
      </section>

      {children}

      {html ? (
        <section>
          <div
            className="lnat-ed-container lnat-ed-body"
            onClick={onBodyClick}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </section>
      ) : null}

      <Footer />
      <ScrollToTopButton />
    </div>
  );
}
